import { Outlet } from 'react-router-dom';

import Breadcrumb from './Breadcrumb';
import NavRail from './NavRail';
import TopBar from './TopBar';
import EnvironmentDriftBanner from './EnvironmentDriftBanner';
import EnvironmentProbe from './EnvironmentProbe';
import RouteLoader from './RouteLoader';
import SlowLoadNotice from './SlowLoadNotice';
import NoEnvironmentAccess from './NoEnvironmentAccess';
import { EnvironmentNotice, useEnvironment } from '../context/EnvironmentContext';
import { PageActionsProvider } from '../context/PageActionsContext';
import { LAYOUT, SURFACE } from '../theme/tokens';

// The AirBridge shell: navy rail on the left, light top bar and content on the
// right (FD-ADR-043, promoted from the preview in FD-466).
//
// It supersedes FD-ADR-031 §1's dark top bar plus sectioned nav tree, and it
// keeps that shell's composition piece for piece — EnvironmentProbe,
// RouteLoader, EnvironmentNotice, EnvironmentDriftBanner, the 1400px content
// column. Each of those is a behaviour the previous shell earned, and a rebrand
// that quietly dropped one would be a regression wearing new colours. What
// changed is the CHROME, not what the chrome does.
//
// ONE THING THE OLD SHELL HAD IS DELIBERATELY GONE: the framer-motion mount-time
// entry spring. It wrapped the whole routed subtree and was never keyed on the
// pathname (deliberately — keying remounts every page's queries), so it played
// exactly once per session, on the first page an operator opened. A one-shot
// animation nobody sees twice is cost without effect; RouteLoader is what
// actually gives per-navigation feedback, and it stays.
//
// The FD-188 rule holds here too: no route-conditional hook in this component.
// There is no full-bleed exception and no route family that renders differently,
// which is why there is no `useMatch` in this file at all.
//
// `relative` on <main> is load-bearing: RouteLoader positions itself
// `absolute inset-0` against it. Without it the loader anchors to the VIEWPORT
// and becomes a full-screen flash on every navigation — a bug the prototype
// shipped twice, and the reason the class is called out here rather than left to
// look decorative.

// ── FD-586: the routed page waits for the active environment to answer ─────
//
// On a multi-environment console the page is not mounted until the ACTIVE
// environment's boot probe has settled — reachable, forbidden, failed, or past
// `PROBE_SETTLE_TIMEOUT_MS`. Before this, a customer admin landed on
// `environments[0]` (Rubix's own), the page's queries fired, and the first thing
// they saw was "Could not load the workspace / This administrator's tenant is not
// permitted" — an environment they should never have been shown, refusing them,
// in the same frame. Not mounting the page is the only version of that fix that
// cannot flash: a page that mounts and is then swapped has already sent its
// requests.
//
// What they see instead is the route loader. When the probe says forbidden, the
// provider lands them on the first environment that admits them and the page
// mounts there; when every environment has refused, `NoEnvironmentAccess` says so
// in one sentence rather than letting one environment's error stand for all of
// them.
//
// A single-environment console never waits — `environmentResolved` is
// constantly true there — so Mode 1 is byte-identical. The rail, the top bar,
// the breadcrumb and both environment banners render throughout: only the
// content that would call the Manager is held back.
export default function AppShell() {
  const { environmentResolved, noAccessibleEnvironment } = useEnvironment();
  const holdForEnvironment = !environmentResolved && !noAccessibleEnvironment;

  let content;
  if (noAccessibleEnvironment) content = <NoEnvironmentAccess />;
  else if (holdForEnvironment) content = null;
  else content = <Outlet />;

  return (
    // FD-470 — the provider wraps BOTH the top bar and the routed content,
    // because that is the boundary a page's CTA has to cross to reach the bar.
    // It carries a DOM node and nothing else; see PageActionsContext for why it
    // is a portal rather than a registered node.
    <PageActionsProvider>
    <div className="flex h-[100dvh]" style={{ backgroundColor: SURFACE.canvas }}>
      {/* The per-environment `GET /v1/whoami` boot probe. Renders nothing;
          mounted inside AuthenticatedTemplate's subtree, which is what
          guarantees MSAL has an account to acquire a token for. */}
      <EnvironmentProbe />
      <NavRail />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="relative min-h-0 flex-1 overflow-y-auto">
          <RouteLoader hold={holdForEnvironment} />
          <div
            data-testid="content-column"
            className="mx-auto w-full px-8 py-6"
            style={{ maxWidth: LAYOUT.contentMaxWidth }}
          >
            <Breadcrumb />
            <EnvironmentNotice />
            <EnvironmentDriftBanner />
            <div className="mt-4">{content}</div>
          </div>
          {/* FD-596 — after ~5 s of a first load (or of the landing gate), one
              calm line saying a cold service can take up to a minute. Mounted
              once here so every page has it; see SlowLoadNotice. */}
          <SlowLoadNotice hold={holdForEnvironment} />
        </main>
      </div>
    </div>
    </PageActionsProvider>
  );
}
